import { FC, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { distinctEffectCodes } from "@/helpers/activeEffects"
import { parseAxiosError } from "@/helpers/parseAxiosError"
import { generateUuid } from "@/helpers/uuid"
import { useCatalogs } from "@/hooks/useCatalogs"
import { useHeroVitalsContext } from "@/providers/HeroVitals"
import {
  useAllocateAttributePointsMutation,
  useDevGrantXpMutation,
  useDevResetHeroMutation,
  useDevSetEffectRemainingMutation,
  useDevVitalAddMutation,
  useDevVitalRemoveMutation,
  useDevVitalSetMutation,
  useGetHeroMeQuery
} from "@/store/apis/heroAPI"
import styles from "./DebugPanel.module.scss"
import { IDebugPanelProps } from "./types"

const DEV_EFFECT_REMAINING_MAX_SECONDS = 30 * 24 * 60 * 60

export const DebugPanel: FC<IDebugPanelProps> = ({ playerId }) => {
  const { t } = useTranslation("hero")
  const { stats } = useCatalogs()
  const attributeCodes = stats
    .filter((entry) => entry.group === "ATTRIBUTE")
    .map((entry) => entry.code)
  const [grantXp] = useDevGrantXpMutation()
  const [resetHero] = useDevResetHeroMutation()
  const [allocate] = useAllocateAttributePointsMutation()
  const vitalCodes = useHeroVitalsContext().map((vital) => vital.code)
  const [addVital] = useDevVitalAddMutation()
  const [removeVital] = useDevVitalRemoveMutation()
  const [setVital] = useDevVitalSetMutation()
  const [setEffectRemaining] = useDevSetEffectRemainingMutation()
  const { activeEffects } = useGetHeroMeQuery(undefined, {
    selectFromResult: ({ data }) => ({ activeEffects: data?.activeEffects })
  })
  const effectCodes = useMemo(
    () => distinctEffectCodes(activeEffects),
    [activeEffects]
  )

  const [amount, setAmount] = useState(100)
  const [statCode, setStatCode] = useState("")
  const [points, setPoints] = useState(1)
  const [vitalCode, setVitalCode] = useState("")
  const [vitalAmount, setVitalAmount] = useState(10)
  const selectedVital = vitalCode || vitalCodes[0] || ""
  const vitalAmountValid = Number.isInteger(vitalAmount) && vitalAmount >= 0
  const [effectCode, setEffectCode] = useState("")
  const [effectSeconds, setEffectSeconds] = useState(0)
  const selectedEffect = effectCodes.includes(effectCode)
    ? effectCode
    : effectCodes[0] || ""
  const effectSecondsValid =
    Number.isInteger(effectSeconds) &&
    effectSeconds >= 0 &&
    effectSeconds <= DEV_EFFECT_REMAINING_MAX_SECONDS
  const [status, setStatus] = useState("")

  // Mutations resolve to {data}|{error} without throwing — unwrap() to surface
  // the server rejection (409 bank / 422 unavailable / …) in the status line.
  const runAction = (action: { unwrap: () => Promise<unknown> }) => {
    setStatus("")
    void action.unwrap().catch((err: unknown) => {
      setStatus(parseAxiosError(err).message || t("error.generic"))
    })
  }

  return (
    <div className={styles.DebugPanel}>
      <strong>DEBUG</strong>

      <fieldset>
        <legend>Grant XP</legend>
        <input
          type="number"
          min={1}
          value={amount}
          onChange={(e) => setAmount(Math.max(1, Number(e.target.value)))}
        />
        <button
          type="button"
          disabled={!Number.isInteger(amount) || amount < 1}
          onClick={() => runAction(grantXp({ playerId, amount }))}
        >
          Grant
        </button>
      </fieldset>

      <fieldset>
        <legend>Reset hero</legend>
        <button
          type="button"
          onClick={() => runAction(resetHero({ playerId }))}
        >
          Reset
        </button>
      </fieldset>

      <fieldset>
        <legend>Spend points</legend>
        <select
          value={statCode || attributeCodes[0] || ""}
          onChange={(e) => setStatCode(e.target.value)}
        >
          {attributeCodes.map((code) => (
            <option key={code} value={code}>
              {code}
            </option>
          ))}
        </select>
        <input
          type="number"
          min={1}
          value={points}
          onChange={(e) => setPoints(Math.max(1, Number(e.target.value)))}
        />
        <button
          type="button"
          disabled={
            !Number.isInteger(points) ||
            points < 1 ||
            attributeCodes.length === 0
          }
          onClick={() =>
            runAction(
              allocate({
                requestId: generateUuid(),
                allocations: [
                  { statCode: statCode || attributeCodes[0], points }
                ]
              })
            )
          }
        >
          Spend
        </button>
      </fieldset>

      <fieldset>
        <legend>Vitals</legend>
        <select
          value={selectedVital}
          onChange={(e) => setVitalCode(e.target.value)}
        >
          {vitalCodes.map((code) => (
            <option key={code} value={code}>
              {code}
            </option>
          ))}
        </select>
        <input
          type="number"
          min={0}
          value={vitalAmount}
          onChange={(e) => setVitalAmount(Math.max(0, Number(e.target.value)))}
        />
        <button
          type="button"
          disabled={!selectedVital || !vitalAmountValid}
          onClick={() =>
            runAction(
              setVital({
                playerId,
                vitalCode: selectedVital,
                value: vitalAmount
              })
            )
          }
        >
          Set
        </button>
        <button
          type="button"
          disabled={!selectedVital || !vitalAmountValid || vitalAmount < 1}
          onClick={() =>
            runAction(
              addVital({
                playerId,
                vitalCode: selectedVital,
                amount: vitalAmount
              })
            )
          }
        >
          Add
        </button>
        <button
          type="button"
          disabled={!selectedVital || !vitalAmountValid || vitalAmount < 1}
          onClick={() =>
            runAction(
              removeVital({
                playerId,
                vitalCode: selectedVital,
                amount: vitalAmount
              })
            )
          }
        >
          Remove
        </button>
      </fieldset>

      <fieldset>
        <legend>Effects</legend>
        <select
          value={selectedEffect}
          onChange={(e) => setEffectCode(e.target.value)}
        >
          {effectCodes.map((code) => (
            <option key={code} value={code}>
              {code}
            </option>
          ))}
        </select>
        <input
          type="number"
          min={0}
          max={DEV_EFFECT_REMAINING_MAX_SECONDS}
          value={effectSeconds}
          onChange={(e) =>
            setEffectSeconds(Math.max(0, Number(e.target.value)))
          }
        />
        <button
          type="button"
          disabled={!selectedEffect || !effectSecondsValid}
          onClick={() =>
            runAction(
              setEffectRemaining({
                playerId,
                effectCode: selectedEffect,
                seconds: effectSeconds
              })
            )
          }
        >
          Set
        </button>
      </fieldset>

      {status && <span className={styles.DebugPanel_status}>{status}</span>}
    </div>
  )
}
